import { useEffect } from 'react';

/**
 * TooltipLayer — 全站唯一的懸停提示框(R393;CIS「提示框:全站只一種」)
 * -----------------------------------------------------------------------------
 * 對齊 D3 `tooltip.js`(v1.9)+ `tooltip.css` `.mtip`:**寫 `title="…"` 就自動升級成
 * `.mtip` 浮層**(深底 #0F172A / 深色 #202C3E、字 12/1.55、padding 9/12、圓角 9、最大寬 270、
 * `pre-line`、跟著游標、250ms 延遲)。元件端不用改任何一行 —— 原本的 `title` 照寫。
 *
 * 跳過(沿 D3 SKIP):`[data-tip]`(自帶提示邏輯)/ `[data-lt]` / `[data-notip]`(要原生行為或
 * 根本不要提示的地方自己標)。說明型提示請用 `ColInfoIcon`(`.info-ic`),不要另寫第二種。
 *
 * ## 與 React 共處(D3 版本不需要考慮的那一半)
 * D3 第一次 hover 就把 `title` 搬進 `data-tiptext`、**永久**移除 `title`。React 管理的 DOM
 * 不能這樣做:React 只在 prop 改變時才寫屬性,而它比對的是**上一次 render 的 prop**,不是 DOM ——
 * 永久移除之後,若 prop 從 'A' 變成 `undefined`,React 呼叫 `removeAttribute` 是 no-op,我們
 * 自己存的那份 'A' 就變成永遠擦不掉的過期提示(例:「已達上限」的提示在解除上限後還在)。
 * 所以這裡只在**滑鼠停在那個元素上的期間**壓制原生提示:
 *   - 壓制 = 把文字存進 `data-hai-tip`,`title` 改成 `""`(空 title 不出原生泡泡,但屬性仍在)
 *   - 離開 = 若 `title` 仍是我們設的 `""` → 還原;**被 React 拿掉(null)→ 不還原**;
 *     被 React 換成新字 → 保留新字(那才是 prop 的真相)
 *   - 壓制期間用 MutationObserver 盯 `title`:React 寫了新字 → 立刻改顯新字並再壓一次
 *     (點一下會改自己 title 的 toggle 鈕就是這種)
 * a11y:不像 D3 那樣替元素補 `aria-label`(會把按鈕自己的文字名稱蓋掉);離開後 `title`
 * 原樣回來,描述不遺失。
 *
 * ## R406 — 清單型內容(同一個提示框,不是第二種)
 * owner 2026-10-09:素材成效「群組名稱」滑過去時浮出的名稱「全都擠在一起,才需要區塊呈現」
 * (原本是 `title` 用「、」串成一長串)。元素多帶 {@link TIP_LIST_ATTR}(JSON 字串陣列)→ 提示框裡
 * **每個項目一個區塊、底色兩色交替**,可選 {@link TIP_NOTE_ATTR} 補一行說明(例「依花費排序,僅列出前
 * 20 個」)。外框 / 字級 / 延遲 / 跟游標全部沿用本層 —— CIS「提示框全站只一種」不破。
 * `title` 照寫(串好的那一串):它是 a11y 描述與沒掛本層時的原生退路,本層只是換一種畫法。
 * ⚠️ 內容一律 `textContent` 組 DOM,**不用 innerHTML**(群組名是外部資料)。
 */

const SKIP = '[data-tip],[data-lt],[data-notip]';
/** R406 — 清單型提示:JSON 字串陣列(每項一個區塊)。 */
export const TIP_LIST_ATTR = 'data-tip-list';
/** R406 — 清單型提示底下的補充說明(一行小字)。 */
export const TIP_NOTE_ATTR = 'data-tip-note';
/** R406 — 清單區塊的兩色交替(深底提示框上:青綠調 / 中性灰調;字色沿用 --tooltip-fg)。 */
export const TIP_ITEM_BG = [
  'color-mix(in oklab, var(--concept-accent) 34%, transparent)',
  'color-mix(in oklab, var(--tooltip-fg) 12%, transparent)',
] as const;
const STASH = 'data-hai-tip';
const SHOW_DELAY_MS = 250;

/** 找出要提示的元素(往上找最近的帶 title / 壓制中的元素;落在 SKIP 子樹內就不提示)。 */
export function pickTipTarget(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element)) return null;
  const el = target.closest<HTMLElement>(`[title],[${STASH}]`);
  if (!el || el.closest(SKIP)) return null;
  return el;
}

/** 讀提示文字,並壓制原生泡泡(`title` → `""`,原字存進 STASH)。 */
export function takeTipText(el: HTMLElement): string {
  const t = el.getAttribute('title');
  if (t) {
    el.setAttribute(STASH, t);
    el.setAttribute('title', '');
    return t;
  }
  return el.getAttribute(STASH) ?? '';
}

/** 還原 `title`(只在它仍是我們設的空字串時;被 React 拿掉或改寫就尊重 React)。 */
export function releaseTip(el: HTMLElement): void {
  const stash = el.getAttribute(STASH);
  if (stash === null) return;
  el.removeAttribute(STASH);
  if (el.getAttribute('title') === '') el.setAttribute('title', stash);
}

/** R406 — 讀清單(格式不合 / 空陣列 → null,退回純文字)。 */
export function readTipList(el: Element): string[] | null {
  const raw = el.getAttribute(TIP_LIST_ATTR);
  if (!raw) return null;
  try {
    const v: unknown = JSON.parse(raw);
    if (!Array.isArray(v) || v.length === 0 || !v.every((x) => typeof x === 'string')) return null;
    return v as string[];
  } catch {
    return null;
  }
}

/**
 * 提示框內容:有清單 → 區塊(兩色交替)+ 選填說明;沒有 → 純文字(原行為)。
 * ⚠️ 只用 `textContent`(外部資料不可走 innerHTML)。
 */
export function renderTipContent(tip: HTMLElement, el: Element, text: string): void {
  const list = readTipList(el);
  if (!list) {
    tip.textContent = text;
    return;
  }
  tip.textContent = '';
  const box = document.createElement('div');
  box.setAttribute('data-testid', 'mtip-list');
  Object.assign(box.style, { display: 'flex', flexDirection: 'column', gap: '4px', whiteSpace: 'normal' });
  list.forEach((item, i) => {
    const row = document.createElement('div');
    row.textContent = item;
    row.setAttribute('data-testid', 'mtip-item');
    Object.assign(row.style, {
      padding: '3px 8px',
      borderRadius: '6px',
      overflowWrap: 'anywhere',
      background: TIP_ITEM_BG[i % TIP_ITEM_BG.length],
    });
    box.appendChild(row);
  });
  tip.appendChild(box);
  const note = el.getAttribute(TIP_NOTE_ATTR);
  if (note) {
    const n = document.createElement('div');
    n.textContent = note;
    n.setAttribute('data-testid', 'mtip-note');
    Object.assign(n.style, {
      marginTop: '6px',
      fontSize: '11.5px',
      whiteSpace: 'normal',
      color: 'color-mix(in oklab, var(--tooltip-fg) 72%, transparent)',
    });
    tip.appendChild(n);
  }
}

function applyTipStyle(tip: HTMLDivElement) {
  const s = tip.style;
  s.position = 'fixed';
  s.zIndex = '10000';
  s.maxWidth = '270px';
  s.background = 'var(--tooltip-bg)';
  s.color = 'var(--tooltip-fg)';
  s.border = '1px solid var(--tooltip-line)';
  s.fontSize = '12px';
  s.lineHeight = '1.55';
  s.padding = '9px 12px';
  s.borderRadius = '9px';
  s.boxShadow = '0 10px 28px -8px color-mix(in oklab, var(--overlay-ink) 50%, transparent)';
  s.whiteSpace = 'pre-line';
  s.pointerEvents = 'none';
  s.fontFamily = 'var(--font-sans)';
  s.fontWeight = '400';
  s.textAlign = 'left';
  s.display = 'none';
}

export function TooltipLayer() {
  useEffect(() => {
    const tip = document.createElement('div');
    tip.className = 'mtip';
    tip.setAttribute('role', 'tooltip');
    tip.setAttribute('data-testid', 'mtip');
    applyTipStyle(tip);
    document.body.appendChild(tip);

    /** 目前壓制中的元素(滑鼠停在它上面);`shown` = 浮層是否正在顯示它。 */
    let active: HTMLElement | null = null;
    let shown = false;
    /** 按下 / 捲動 / Esc 之後先收起,直到滑鼠離開這個元素前都不再自動跳出。 */
    let dismissed = false;
    /** 最近一次焦點來自滑鼠點擊(不是鍵盤)→ 那次 focusin 不跳提示(點一下按鈕不該冒出說明)。 */
    let pointerFocus = false;
    let timer: number | undefined;
    let observer: MutationObserver | null = null;

    const place = (x: number, y: number) => {
      const r = tip.getBoundingClientRect();
      const left = Math.min(x + 14, window.innerWidth - r.width - 10);
      let top = y + 18;
      if (top + r.height > window.innerHeight - 10) top = y - r.height - 14;
      tip.style.left = `${Math.max(10, left)}px`;
      tip.style.top = `${Math.max(10, top)}px`;
    };
    const hideVisual = () => {
      window.clearTimeout(timer);
      shown = false;
      tip.style.display = 'none';
    };
    const deactivate = () => {
      hideVisual();
      observer?.disconnect();
      observer = null;
      if (active) releaseTip(active);
      active = null;
      dismissed = false;
    };
    const showFor = (el: HTMLElement, x: number, y: number) => {
      const text = takeTipText(el);
      if (!text) {
        hideVisual();
        return;
      }
      renderTipContent(tip, el, text);
      tip.style.display = 'block';
      shown = true;
      place(x, y);
    };
    const activate = (el: HTMLElement) => {
      active = el;
      dismissed = false;
      // 先壓制(停留期間不讓原生泡泡出現),浮層等延遲後才顯示。
      takeTipText(el);
      observer = new MutationObserver(() => {
        if (active !== el) return;
        const t = el.getAttribute('title');
        if (t) {
          // React 寫了新字 → 改顯新字並再壓一次
          el.setAttribute(STASH, t);
          el.setAttribute('title', '');
          if (shown) renderTipContent(tip, el, t);
        } else if (t === null && el.getAttribute(STASH) !== null) {
          // React 把 title 拿掉了 → 這個元素不再有提示
          el.removeAttribute(STASH);
          hideVisual();
        }
      });
      observer.observe(el, { attributes: true, attributeFilter: ['title'] });
    };

    const onMove = (e: MouseEvent) => {
      const el = pickTipTarget(e.target);
      if (el && el === active) {
        if (shown) place(e.clientX, e.clientY);
        return;
      }
      deactivate();
      if (!el) return;
      activate(el);
      const { clientX: x, clientY: y } = e;
      timer = window.setTimeout(() => {
        if (active === el && !dismissed) showFor(el, x, y);
      }, SHOW_DELAY_MS);
    };
    const onDismiss = () => {
      if (!active) return;
      dismissed = true;
      hideVisual();
    };
    const onPointerDown = () => {
      pointerFocus = true;
      onDismiss();
    };
    const onKey = (e: KeyboardEvent) => {
      pointerFocus = false;
      if (e.key === 'Escape') onDismiss();
    };
    // 鍵盤 Tab 到帶 title 的元素 → 在元素下緣顯示(D3 focusin 同語意);滑鼠點出來的焦點不顯。
    const onFocusIn = (e: FocusEvent) => {
      if (pointerFocus) return;
      const el = pickTipTarget(e.target);
      if (!el) return;
      deactivate();
      activate(el);
      const r = el.getBoundingClientRect();
      showFor(el, r.left + Math.min(r.width / 2, 80), r.bottom - 6);
    };
    // 只在「失焦的就是目前提示的那個元素」時收 —— 否則滑鼠正停在別的元素上時,Tab 一下就把
    // 那個元素的壓制解掉,原生泡泡會冒出來。
    const onFocusOut = (e: FocusEvent) => {
      if (active && pickTipTarget(e.target) === active) deactivate();
    };
    const onLeaveDoc = (e: MouseEvent) => {
      if (!e.relatedTarget) deactivate();
    };

    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('mouseout', onLeaveDoc, true);
    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('wheel', onDismiss, { passive: true, capture: true });
    document.addEventListener('scroll', onDismiss, true);
    document.addEventListener('keydown', onKey);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    window.addEventListener('blur', deactivate);
    return () => {
      deactivate();
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('mouseout', onLeaveDoc, true);
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('wheel', onDismiss, { capture: true } as EventListenerOptions);
      document.removeEventListener('scroll', onDismiss, true);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      window.removeEventListener('blur', deactivate);
      tip.remove();
    };
  }, []);
  return null;
}
